import {Component, OnDestroy, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {LangChangeEvent, TranslateService} from '@ngx-translate/core';
import {QueryRef} from 'apollo-angular';
import {Subscription} from 'rxjs';
import {BaseComponent} from '../../base/base.component';
import {UserService} from '../../service/user.service';
import {FilterService} from '../../service/filter.service';
import {
    Country,
    GetCountriesGQL,
    GetCountriesQuery,
    GetUnitiesByCountryGQL,
    GetUnitiesByCountryQuery,
    Unity,
    User
} from '../../../generated/graphql';
import {
    MOCK_DAILY_FLOW,
    MOCK_KPIS,
    MOCK_PROVIDER_SERVICE_STATUS,
    MOCK_TOP_PROVIDER_COMPANIES,
    MOCK_TRUCK_CHECKLIST_NC,
    MOCK_TRUCKS_BY_REASON
} from './dashboard-clipboard.mock';

declare var moment;
declare var $;

interface KpiCard {
    label: string;
    value: string;
    hint: string;
    variant: 'default' | 'success' | 'warning' | 'danger';
}

@Component({
    selector: 'app-dashboard-clipboard',
    templateUrl: './dashboard-clipboard.component.html',
    styleUrls: ['./dashboard-clipboard.component.scss']
})
export class DashboardClipboardComponent extends BaseComponent implements OnInit, OnDestroy {

    filterKey = 'dashboard_clipboard_filter';
    user: User = new User();

    subscription: Subscription;

    countries: Array<Country>;
    countryQuery: QueryRef<GetCountriesQuery>;

    unities: Array<Unity>;
    unityQuery: QueryRef<GetUnitiesByCountryQuery>;

    selectedCountryId: string;
    filter: any = {};

    kpiCards: KpiCard[] = [];

    myData1: any[] = [['', 0, 0]];      // fluxo diário
    myData2: any[] = [['', 0, 0, 0]];   // caminhões por status e motivo
    myData3: any[] = [['', 0]];         // não-conformidades checklist
    myData4: any[] = [['', 0]];         // serviços por status
    myData5: any[] = [['', 0]];         // top empresas

    chart1Names = ['Dia', 'Caminhões', 'Prestadores'];
    chart1NamesES = ['Día', 'Camiones', 'Prestadores'];

    chart2Names = ['Motivo', 'Aguardando inspeção', 'Aguardando liberação', 'Finalizado'];
    chart2NamesES = ['Motivo', 'Esperando inspección', 'Esperando liberación', 'Finalizado'];

    chart3Names = ['Item', 'Não conformidades'];
    chart3NamesES = ['Ítem', 'No conformidades'];

    chart4Names = ['Status', 'Quantidade'];
    chart4NamesES = ['Estado', 'Cantidad'];

    chart5Names = ['Empresa', 'Visitas'];
    chart5NamesES = ['Empresa', 'Visitas'];

    columnNames1: string[] = [];
    columnNames2: string[] = [];
    columnNames3: string[] = [];
    columnNames4: string[] = [];
    columnNames5: string[] = [];

    options1 = {legend: {position: 'top'}, vAxis: {minValue: 0, format: '0', gridlines: {count: -1}}, colors: ['#032E58', '#004B93']};
    options2 = {
        isStacked: 'percent',
        legend: {position: 'top'},
        vAxis: {minValue: 0, maxValue: 1, format: 'percent', gridlines: {count: 5}},
        colors: ['#004B93', '#FAE100', '#032E58']
    };
    options3 = {legend: {position: 'none'}, hAxis: {minValue: 0, format: '0', gridlines: {count: -1}}, colors: ['#C0392B']};
    options4 = {legend: {position: 'right'}, colors: ['#004B93', '#FAE100', '#032E58', '#C0392B']};
    options5 = {
        legend: {position: 'none'},
        hAxis: {minValue: 0, format: '0', gridlines: {count: -1}},
        bar: {groupWidth: '45%'},
        colors: ['#004B93']
    };

    constructor(public router: Router,
                public translate: TranslateService,
                public userService: UserService,
                private getCountriesGQL: GetCountriesGQL,
                private getUnitiesByCountryGQL: GetUnitiesByCountryGQL,
                private filterService: FilterService) {
        super(router, translate);
        this.subscription = this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
            $(() => {
                this.checkSubtitle();
                this.buildKpis();
                this.buildCharts();
            });
        });
    }

    ngOnInit(): void {
        this.userService.user.subscribe({
            next: data => {
                this.user = data as User;
            }, error: err => super.onError(err)
        });

        this.getCountries();
        this.getUnitiesInit();

        const savedFilter = this.filterService.getFilter(this.filterKey);
        if (savedFilter) {
            this.filter = savedFilter;
            this.selectedCountryId = savedFilter.countryId;
            if (savedFilter.countryId) {
                this.getUnities();
            }
        } else {
            this.setDefaultPeriod();
        }

        this.initDatePicker();

        this.checkSubtitle();
        this.buildKpis();
        this.buildCharts();
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    setDefaultPeriod() {
        this.filter.start = moment().startOf('day').subtract(30, 'days').utcOffset(0, true).format();
        this.filter.end = moment().endOf('day').utcOffset(0, true).format();
    }

    initDatePicker() {
        this.setDatePickerRange('dashboard-daterangepicker', (start, end, label) => {
            this.filter.start = start.utcOffset(0, true).format();
            this.filter.end = end.utcOffset(0, true).format();
        }, this.filter.start, this.filter.end);
    }

    getCountries() {
        this.countryQuery = this.getCountriesGQL.watch();
        this.countryQuery.valueChanges.subscribe(({data}) => {
            this.countries = data.countries as Array<Country>;
        });
    }

    getUnitiesInit() {
        this.unityQuery = this.getUnitiesByCountryGQL.watch({countryId: this.isNullOrUndefined(this.selectedCountryId) ? '' : this.selectedCountryId});
        this.unityQuery.valueChanges.subscribe(({data}) => {
            this.unities = data.getUnitiesByCountry as Array<Unity>;
        });
    }

    getUnities() {
        this.unityQuery.refetch({countryId: this.isNullOrUndefined(this.selectedCountryId) ? '' : this.selectedCountryId});
    }

    applyFilter() {
        if (!this.isNullOrUndefined(this.selectedCountryId)) {
            this.filter.countryId = this.selectedCountryId;
        } else {
            this.filter.countryId = undefined;
        }
        this.filterService.setFilter(this.filterKey, this.filter);

        super.toggleLoader(true);
        this.buildKpis();
        this.buildCharts();
        super.toggleLoader(false);
        // TODO(dados-reais): aplicar filtro
    }

    clearFilter() {
        this.filterService.clearFilter(this.filterKey);
        this.filter = {};
        this.selectedCountryId = null;
        this.setDefaultPeriod();
    }

    // ---------------------------------------------------------------------
    buildKpis() {
        const k = MOCK_KPIS;
        const t = (key: string) => this.translate.instant('dashboards.clipboard.' + key);
        this.kpiCards = [
            {label: t('kpi.trucksInPeriod'), value: this.fmtInt(k.trucksInPeriod), hint: '', variant: 'default'},
            {label: t('kpi.trucksInYard'), value: this.fmtInt(k.trucksInYardNow), hint: '', variant: 'warning'},
            {label: t('kpi.avgDwell'), value: this.fmtDuration(k.avgDwellMinutes), hint: '', variant: 'default'},
            {label: t('kpi.scheduledProviders'), value: this.fmtInt(k.scheduledProviders), hint: '', variant: 'default'},
            {label: t('kpi.expiredServices'), value: this.fmtInt(k.expiredServices), hint: '', variant: 'danger'},
            {label: t('kpi.divergentSeals'), value: this.fmtInt(k.divergentSealsOnExit), hint: '', variant: 'danger'},
        ];
    }

    buildCharts() {
        this.myData1 = MOCK_DAILY_FLOW.map(r => [r.day, r.trucks, r.providers]);
        this.myData2 = MOCK_TRUCKS_BY_REASON.map(r => [this.reasonLabel(r.reason), r.inspect, r.release, r.finished]);
        this.myData3 = MOCK_TRUCK_CHECKLIST_NC.map(r => [r.item, r.count]);
        this.myData4 = MOCK_PROVIDER_SERVICE_STATUS.map(r => [this.serviceStatusLabel(r.status), r.count]);
        this.myData5 = MOCK_TOP_PROVIDER_COMPANIES.map(r => [r.company, r.visits]);
    }

    checkSubtitle() {
        if (this.translate.currentLang === 'pt') {
            this.columnNames1 = this.chart1Names;
            this.columnNames2 = this.chart2Names;
            this.columnNames3 = this.chart3Names;
            this.columnNames4 = this.chart4Names;
            this.columnNames5 = this.chart5Names;
        } else {
            this.columnNames1 = this.chart1NamesES;
            this.columnNames2 = this.chart2NamesES;
            this.columnNames3 = this.chart3NamesES;
            this.columnNames4 = this.chart4NamesES;
            this.columnNames5 = this.chart5NamesES;
        }
    }

    reasonLabel(reason: string): string {
        return this.translate.instant('dashboards.clipboard.reasons.' + reason);
    }

    serviceStatusLabel(status: string): string {
        return this.translate.instant('dashboards.clipboard.serviceStatus.' + status);
    }

    fmtInt(n: number): string {
        return n.toString();
    }

    fmtDuration(minutes: number): string {
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        return `${h}h${m < 10 ? '0' + m : m}`;
    }
}
